/**
 * MobiliHeaderToolbar — the calendar toolbar REBUILT for the MobiliCalendar
 * engine, unencumbered by FullCalendar. Deliberately NOT a clone of
 * molecules/Calendar/HeaderToolbar: that one is coupled to a FullCalendar ref
 * (gotoDate), cell-selection capture and the mode-switch calendars. Here the
 * CalendarStore is the single source of truth — date navigation just writes
 * `selectedDateStr` and everything (controller queries, engine) reacts.
 *
 * shadcn/ui only (house direction: away from MUI). Weekend-skipping date
 * math is ported 1:1 from the old toolbar so j/k-style stepping behaves
 * identically. Deferred until their features exist in the new shell: view
 * mode toggle (Dauertermin/Arbeitszeiten), TherapistSelect/UserSelector,
 * Vorlagen expander, Backplan.
 */
import { useQueryClient } from "@tanstack/react-query";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ExternalLink,
  PanelBottom,
  PanelTop,
  RefreshCw,
  Rocket,
} from "lucide-react";
import { observer } from "mobx-react";
import moment from "moment";
import React from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import useStore from "../../helpers/useStore";
import { MCStats } from "../../molecules/MobiliCalendar/model/types";
import { nextDateStr, previousDateStr } from "../../molecules/Calendar/dateStepping";
import { invalidateAllCalendarQueries } from "../../stores/Calendar/calendar.keys";
import { CalendarContext } from "../../stores/Calendar/calendar.provider";
import { ShelfPlacement } from "./useShelfPlacement";

interface MobiliHeaderToolbarProps {
  stats?: MCStats | null;
  /** Current shelf position; the toggle below lets us A/B the two layouts. */
  shelfPlacement?: ShelfPlacement;
  onShelfPlacementChange?: (next: ShelfPlacement) => void;
}

/** Icon button with tooltip — the toolbar's repeating unit. */
const IconAction: React.FC<{
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ label, onClick, children }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Button variant="outline" className="h-8 w-8 p-0" onClick={onClick} aria-label={label}>
        {children}
      </Button>
    </TooltipTrigger>
    <TooltipContent>{label}</TooltipContent>
  </Tooltip>
);

/**
 * Weekday selector for Dauertermin — a segmented control over Mo–Fr.
 *
 * In continuous view the ONLY meaningful navigation axis is which weekday's
 * template you are looking at, and there are exactly five of them. Stepping
 * through that with ‹ › hides the choice behind a hidden cursor; showing all
 * five makes the option set and the current position self-evident in one
 * glance, and every day is one click away.
 *
 * Built from the shadcn Button rather than a ToggleGroup: the project has no
 * toggle-group component and no @radix-ui/react-toggle-group dependency, and
 * a five-item segmented control does not justify adding one.
 *
 * Selecting a day keeps the ISO week and moves only the weekday
 * (`isoWeekday(n)`), so the underlying date stays sensible even though only
 * its weekday is read in this view.
 */
const WEEKDAYS: ReadonlyArray<{ iso: number; label: string }> = [
  { iso: 1, label: "Montag" },
  { iso: 2, label: "Dienstag" },
  { iso: 3, label: "Mittwoch" },
  { iso: 4, label: "Donnerstag" },
  { iso: 5, label: "Freitag" },
];

const WeekdaySelector: React.FC<{
  selectedDateStr: string;
  onSelect: (dateStr: string) => void;
}> = ({ selectedDateStr, onSelect }) => {
  const activeIso = moment(selectedDateStr).isoWeekday();
  return (
    <div
      role="group"
      aria-label="Wochentag wählen"
      className="inline-flex items-center rounded-md border border-input bg-background p-0.5"
    >
      {WEEKDAYS.map(({ iso, label }) => {
        const isActive = activeIso === iso;
        return (
          <Button
            key={iso}
            variant={isActive ? "default" : "ghost"}
            aria-pressed={isActive}
            className={cn(
              "h-7 rounded-sm px-3 text-xs font-semibold transition-colors",
              !isActive && "text-muted-foreground",
            )}
            onClick={() =>
              onSelect(moment(selectedDateStr).isoWeekday(iso).format("YYYY-MM-DD"))
            }
          >
            {label}
          </Button>
        );
      })}
    </div>
  );
};

const MobiliHeaderToolbar: React.FC<MobiliHeaderToolbarProps> = observer(({
  stats,
  shelfPlacement,
  onShelfPlacementChange,
}) => {
  const CalendarStore = useStore(CalendarContext);
  const queryClient = useQueryClient();
  const {
    selectedDateStr,
    setSelectedDateStr,
    isCancelledView,
    setCancelledView,
    continuousView,
    setContinuousView,
  } = CalendarStore;

  // Weekend-skipping date stepping — shared with the Shift+J/L keyboard
  // paging (molecules/Calendar/dateStepping.ts; spec: datePage).
  const computeNextDate = (days: number) => nextDateStr(selectedDateStr, days);
  const computePreviousDate = (days: number) => previousDateStr(selectedDateStr, days);

  const isToday = selectedDateStr === moment().format("YYYY-MM-DD");

  return (
    <TooltipProvider delayDuration={300}>
      <div className="mb-1 flex w-full flex-wrap items-center gap-2 gap-y-1">
        <Badge className="gap-1 bg-primary font-bold">
          <Rocket className="h-3.5 w-3.5" />
          MobiliCal
        </Badge>

        {/* ── Date navigation ─────────────────────────────────────── */}
        <div className="flex items-center gap-1">
          {/* Week stepping is hidden in Dauertermin view: ±7 days lands on the
              SAME weekday, so both the title and the fetched data would be
              unchanged — the buttons are genuinely inert there. */}
          {!continuousView && (
            <IconAction
              label="Woche zurück"
              onClick={() => setSelectedDateStr(computePreviousDate(7))}
            >
              <ChevronsLeft />
            </IconAction>
          )}
          {/* Day stepping is replaced by the weekday selector in Dauertermin:
              with all five weekdays on screen, stepping blind through them is
              strictly worse than clicking the one you want. */}
          {!continuousView && (
            <IconAction
              label="Tag zurück"
              onClick={() => setSelectedDateStr(computePreviousDate(1))}
            >
              <ChevronLeft />
            </IconAction>
          )}
          <Button
            variant={isToday ? "default" : "outline"}
            className="h-8 px-2.5"
            onClick={() => setSelectedDateStr(moment().format("YYYY-MM-DD"))}
          >
            Heute
          </Button>
          {!continuousView && (
            <IconAction label="Tag vor" onClick={() => setSelectedDateStr(computeNextDate(1))}>
              <ChevronRight />
            </IconAction>
          )}
          {!continuousView && (
            <IconAction label="Woche vor" onClick={() => setSelectedDateStr(computeNextDate(7))}>
              <ChevronsRight />
            </IconAction>
          )}
          {/* Same reason the legacy toolbar hides its date picker in
              Dauertermin: the view is a weekly template keyed on the weekday,
              so picking a specific calendar date is meaningless. */}
          {!continuousView && (
            <input
              type="date"
              value={selectedDateStr}
              onChange={(e) => e.target.value && setSelectedDateStr(e.target.value)}
              className="h-8 rounded-md border border-input bg-background px-2 text-sm"
              aria-label="Datum wählen"
            />
          )}
          <IconAction
            label="Aktualisieren"
            onClick={() => void invalidateAllCalendarQueries(queryClient)}
          >
            <RefreshCw />
          </IconAction>
        </div>

        {/* ── Weekday selector (Dauertermin only) ─────────────────── */}
        {continuousView && (
          <WeekdaySelector
            selectedDateStr={selectedDateStr}
            onSelect={setSelectedDateStr}
          />
        )}

        <Separator orientation="vertical" className="h-6 self-center" />

        {/* ── Current date ────────────────────────────────────────── */}
        {/* Dropped entirely in Dauertermin: the selector already names the
            active weekday in full, so a heading repeating it is noise. In the
            standard view the date is the only place the day is stated. */}
        {!continuousView && (
          <h2 className="min-w-[190px] truncate text-base font-bold">
            {moment(selectedDateStr).format("dddd, D. MMM YYYY")}
          </h2>
        )}

        <div className="flex-1" />

        {/* ── Filters & prototype extras ──────────────────────────── */}
        {/* Dauertermin: swaps the engine's data source to the continuous
            controller (weekly-template events); see viewmodel/continuousDay. */}
        <label className="flex cursor-pointer items-center gap-1.5">
          <Switch checked={continuousView} onCheckedChange={setContinuousView} />
          <span className="text-xs font-medium">Dauertermine</span>
        </label>
        <label className="flex cursor-pointer items-center gap-1.5">
          <Switch checked={isCancelledView} onCheckedChange={setCancelledView} />
          <span className="text-xs font-medium">Stornierungen</span>
        </label>

        {/* Shelf placement A/B — prototype-only, persisted per browser. */}
        {shelfPlacement && onShelfPlacementChange && (
          <IconAction
            label={
              shelfPlacement === "below"
                ? "Ablage & Notizen nach oben"
                : "Ablage & Notizen nach unten"
            }
            onClick={() =>
              onShelfPlacementChange(shelfPlacement === "below" ? "above" : "below")
            }
          >
            {shelfPlacement === "below" ? <PanelBottom /> : <PanelTop />}
          </IconAction>
        )}
        {stats && (
          <span className="text-xs tabular-nums text-muted-foreground">
            {stats.resourceCount} Spalten · {stats.eventCount} Termine ·{" "}
            {stats.backgroundCount} Hintergrund · Index {stats.indexBuildMs.toFixed(1)} ms
          </span>
        )}
        <Button variant="outline" className="h-8 gap-1 px-2.5" asChild>
          <a href="/termin">
            <ExternalLink className="h-3.5 w-3.5" />
            Alter Kalender
          </a>
        </Button>
      </div>
    </TooltipProvider>
  );
});
MobiliHeaderToolbar.displayName = "MobiliHeaderToolbar";

export default MobiliHeaderToolbar;
